import { BalanceData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { chestShare } from "./orders";
import { vipResources } from "./vipLevels";

/**
 * Fortress Tribute (ENGAGEMENT_2026-09-23 B5, DESIGN §10.1; `meta.json tribute`): idle income that fills over
 * `capHours` and then stops, claimed by a tap on the fortress. Full, it holds `chestsWhenFull` average Win chests at
 * the player's highest cleared level. Never sold; VIP levels hold a few hours more at the same rate (docs/SHOP.md §2). The clock starts the first time the hub sees the
 * unlock level cleared; a clock set backwards only pauses it.
 */

export interface TributeJson { since: number }

export class Tribute {
  /** When the current fill started (Unix seconds); −1 until unlocked. */
  since = -1;

  /**
   * VIP longer Tribute (docs/SHOP.md §2, `tributeExtraHours`): seconds added to the cap at the same fill rate (a bigger
   * Tribute, not a slower one). Set by @highmarch/app Deals from the `vip` section; derived, so not saved.
   */
  extraCapSeconds = 0;
  /**
   * VIP resource production (docs/SHOP.md §8.3, `resourcePercent`): a claim pays × (1 + r / 100), each resource rounded
   * down (meta/vipLevels.ts vipResources). Set by @highmarch/app Deals from the `vip` section; derived, so not saved.
   */
  resourcePercent = 0;

  constructor(readonly balance: BalanceData) {}

  /** How long it fills before it stops: the data's cap plus the VIP hours (0 while Tribute is off in the data). */
  get capSeconds(): number {
    const base = this.balance.commerce.tribute.capSeconds;
    return base > 0 ? base + Math.max(0, this.extraCapSeconds) : 0;
  }

  unlocked(highestCleared: number): boolean { return highestCleared >= this.balance.commerce.tribute.unlockAfterLevel && this.balance.commerce.tribute.capSeconds > 0; }

  /** Starts the fill once the unlock level is cleared (the hub calls it on every visit). */
  sync(now: number, highestCleared: number): void {
    if (this.since < 0 && this.unlocked(highestCleared)) this.since = now;
  }

  /** Seconds of fill so far, capped. */
  filledSeconds(now: number): number {
    if (this.since < 0) return 0;
    return Math.min(Math.max(0, now - this.since), this.capSeconds);
  }

  isFull(now: number): boolean { return this.since >= 0 && this.filledSeconds(now) >= this.capSeconds; }

  /**
   * What a claim would pay now at `level`: the data's full Tribute × seconds filled / the data's cap (VIP hours fill on
   * at that rate), then × VIP resource production.
   */
  accrued(now: number, level: number): number {
    return vipResources(this.accruedBase(now, level), this.resourcePercent);
  }

  /** The part of `accrued` VIP resource production adds (the chip's and the claim's "+n VIP"); zero at VIP 0. */
  vipBonus(now: number, level: number): number {
    return this.accrued(now, level) - this.accruedBase(now, level);
  }

  private accruedBase(now: number, level: number): number {
    const t = this.balance.commerce.tribute;
    if (this.since < 0 || t.capSeconds <= 0) return 0;
    const fraction = Fix64.fromInt(this.filledSeconds(now)).divInt(t.capSeconds).mul(t.chestsWhenFull);
    return chestShare(this.balance, Math.max(1, level), fraction);
  }

  /** Takes the Tribute and starts the next fill; null (changing nothing) while it holds nothing yet. */
  claim(now: number, level: number): number | null {
    const pay = this.accrued(now, level);
    if (pay <= 0) return null;
    this.since = now;
    return pay;
  }

  toJson(): TributeJson { return { since: this.since }; }

  static fromJson(balance: BalanceData, j: unknown): Tribute {
    const t = new Tribute(balance);
    const since = (j as Partial<TributeJson> | null)?.since;
    if (typeof since === "number" && Number.isInteger(since) && since >= -1) t.since = since;
    return t;
  }
}
